import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { ApiError } from './client'
import {
  activateUser,
  activateZone,
  archiveZone,
  clearPoliceApiKey,
  verifyPoliceApiKey,
  createChannel,
  createZone,
  getStaffFeedback,
  listStaffFeedback,
  submitFeedback,
  triageFeedback,
  type StaffFeedbackFilters,
  deactivateUser,
  deactivateZone,
  deleteChannel,
  designatePolice,
  disconnectConnection,
  getAccount,
  getInbox,
  getMe,
  getPolice,
  getPoliceModels,
  getSecretEvents,
  getToolingInfo,
  setSecretLifetime,
  setPresenceTimeout,
  listUploads,
  listSecureFiles,
  listUploadDownloads,
  removeUpload,
  setUploadLimit,
  setBlockedMimeTypes,
  getPresence,
  getZoneMcpJson,
  grantZoneMember,
  inviteUser,
  joinChannel,
  joinZone,
  leaveChannel,
  leaveZone,
  listAddons,
  listChannelMembers,
  listChannels,
  listConnections,
  listMessageIndex,
  getBalance,
  getRefundable,
  createRefundRequest,
  listBalanceTransactions,
  getBilling,
  getPoliceRecharge,
  keepZones,
  setPoliceRecharge,
  startCheckout,
  startTopUp,
  getBillingConfig,
  listInvoices,
  payInvoice,
  getUpcoming,
  listPaymentMethods,
  createSetupIntent,
  setDefaultPaymentMethod,
  removePaymentMethod,
  getBillingDetails,
  updateBillingDetails,
  listTaxIds,
  addTaxId,
  deleteTaxId,
  previewPlanChange,
  changePlanNow,
  cancelSubscription,
  resumeSubscription,
  undoScheduledChange,
  listConversations,
  searchMessages,
  archiveConversation,
  closeAccount,
  unarchiveConversation,
  listMessages,
  listPlans,
  listUsers,
  listZoneMembers,
  listZones,
  pauseConnection,
  reissueConnection,
  releaseConnection,
  resignPolice,
  revokeZoneMember,
  rotateToken,
  sendMessage,
  setAddon,
  unpauseConnection,
  updatePolice,
  updateUser,
  updateZone,
} from './endpoints'
import { PROBLEM_TYPES } from './types'
import type {
  StaffFeedbackItem,
  StaffFeedbackPage,
  SubmitFeedbackRequest,
  SubmitFeedbackResponse,
  TriageFeedbackRequest,
  AccountSummary,
  PoliceModelOption,
  SecretEvent,
  DownloadEventDto,
  SecureFileDto,
  UploadsPage,
  ZoneHistoryPage,
  ZoneMessageIndex,
  AccountBalance,
  BillingSummary,
  BillingConfig,
  BillingAddress,
  BillingDetails,
  Invoice,
  InvoiceKind,
  InvoicePage,
  PaymentMethodCard,
  PlanChangePreview,
  PlanChangeResult,
  SubscriptionState,
  TaxId,
  UpcomingInvoice,
  OverLimit,
  PoliceRecharge,
  CheckoutSession,
  SubscribeResult,
  BalanceTransactionRow,
  Refundable,
  RefundRequest,
  ConversationIndex,
  SearchResults,
  AccountAddon,
  AccountResponse,
  AccountUser,
  ChannelMember,
  ChannelSummary,
  ConnectionSummary,
  CreateZoneRequest,
  InviteUserRequest,
  MembershipRole,
  MeResponse,
  Plan,
  PoliceSettings,
  QueuedMessage,
  RotateTokenResponse,
  SendMessageRequest,
  SendMessageResponse,
  ReissueResponse,
  ReleaseResponse,
  ToolingInfo,
  UnpauseConnectionRequest,
  UnpauseResponse,
  UpdatePoliceRequest,
  UpdateZoneRequest,
  ZoneMemberSummary,
  ZoneStateFilter,
  ZoneSummary,
} from './types'

/** Live views poll on this cadence — connections change second to second. */
export const CONNECTIONS_POLL_MS = 5_000
/** The zone conversation is the most live thing in the console. */
export const MESSAGES_POLL_MS = 4_000
const CHANNELS_POLL_MS = 15_000

/** The resolved half-open window `[since, until)`, as `pages/time-window.ts` produces it. */
export interface TimeBounds {
  since?: string
  until?: string
}

/**
 * The window's contribution to a query key: nothing at all when unbounded, so a key with no time
 * filter keeps its existing shape and stays a PREFIX of the windowed ones. Prefixed with `w:` so a
 * window can never be mistaken for the channel segment that may precede it.
 */
function windowSegment(window: TimeBounds | undefined): readonly string[] {
  if (window === undefined) return []
  const { since, until } = window
  if (since === undefined && until === undefined) return []
  return [`w:${since ?? ''}..${until ?? ''}`]
}

export const queryKeys = {
  me: ['me'] as const,
  account: ['account'] as const,
  plans: ['plans'] as const,
  /** `GET /tooling/info` — per server, not per zone; nothing in the key names a zone. */
  toolingInfo: ['tooling', 'info'] as const,
  users: ['users'] as const,
  zones: (state: ZoneStateFilter) => ['zones', state] as const,
  allZones: ['zones'] as const,
  zoneMembers: (zoneId: string | undefined) => ['zones', zoneId, 'members'] as const,
  connections: (zoneId: string | undefined) => ['zones', zoneId, 'connections'] as const,
  channels: (zoneId: string | undefined) => ['zones', zoneId, 'channels'] as const,
  channelMembers: (zoneId: string | undefined, channelId: string | undefined) =>
    ['zones', zoneId, 'channels', channelId, 'members'] as const,
  presence: (zoneId: string | undefined) => ['zones', zoneId, 'presence'] as const,
  // `anchor` re-keys the cache when the jump rail re-centres the window somewhere older, so the
  // previous window is not silently prepended to a set of pages it does not belong to.
  //
  // `channel` and the time window are appended ONLY when set, never defaulted — so a bare
  // `queryKeys.messages(zoneId)`
  // (used by invalidation call sites that know nothing about which channel is selected) stays a
  // PREFIX of every channel-scoped key and invalidates all of them, while a hook that names a
  // channel gets its own cache entry and refetches when the channel changes.
  messages: (zoneId: string | undefined, anchor?: number, channel?: string, window?: TimeBounds) =>
    [
      'zones', zoneId, 'messages', anchor ?? 'latest',
      ...(channel === undefined ? [] : [channel]),
      ...windowSegment(window),
    ] as const,
  messageIndex: (zoneId: string | undefined, channel?: string, window?: TimeBounds) =>
    [
      'zones', zoneId, 'messages', 'index',
      ...(channel === undefined ? [] : [channel]),
      ...windowSegment(window),
    ] as const,
  balance: () => ['account', 'balance'] as const,
  balanceTransactions: () => ['account', 'balance', 'transactions'] as const,
  conversations: (
    zoneId: string | undefined,
    archived: boolean,
    channel?: string,
    window?: TimeBounds,
  ) =>
    [
      'zones', zoneId, 'conversations', archived,
      ...(channel === undefined ? [] : [channel]),
      ...windowSegment(window),
    ] as const,
  search: (zoneId: string | undefined, query: string, archived: boolean) =>
    ['zones', zoneId, 'search', query, archived] as const,
  inbox: (zoneId: string | undefined) => ['zones', zoneId, 'inbox'] as const,
  mcpJson: (zoneId: string | undefined) => ['zones', zoneId, 'tooling', 'mcp.json'] as const,
  police: (zoneId: string | undefined) => ['zones', zoneId, 'police'] as const,
  uploads: (zoneId: string | undefined) => ['zones', zoneId, 'uploads'] as const,
  uploadDownloads: (zoneId: string | undefined, handle: string) =>
    ['zones', zoneId, 'uploads', handle, 'downloads'] as const,
  secureFiles: (zoneId: string | undefined) => ['zones', zoneId, 'secure-files'] as const,
  addons: ['account', 'addons'] as const,
  staffFeedback: (filters: StaffFeedbackFilters) => ['staff', 'feedback', filters] as const,
  staffFeedbackItem: (id: string | undefined) => ['staff', 'feedback', 'item', id] as const,
}

/** Never retry a request the server has definitively rejected. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.status === 401 || error.status === 403 || error.status === 404) return false
    if (error.status >= 400 && error.status < 500) return false
  }
  return failureCount < 2
}

/**
 * True when a write failed *only* because the caller has no HUMAN presence (§18).
 *
 * Discriminates on the problem `type`, not on the bare 409: the server uses 409
 * for a taken name, a quota, an archived row, the last administrator and a
 * connection that is not connected as well, and treating any of those as
 * "join the zone first" hides the real reason behind a recovery action that
 * cannot possibly help. The status-only fallback exists for a 409 that carries
 * no problem body at all (a proxy, a truncated response).
 */
export function isMissingPresence(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  const type = error.problem?.type
  return type === undefined || type === PROBLEM_TYPES.presenceRequired
}

/**
 * Zone-scoped `queryFn` that refuses to run without a zone rather than
 * fabricating a URL. Paired with `enabled`, it never actually rejects.
 */
function zoneQuery<T>(
  zoneId: string | undefined,
  run: (id: string, signal?: AbortSignal) => Promise<T>,
): (context: { signal: AbortSignal }) => Promise<T> {
  return ({ signal }) =>
    zoneId === undefined
      ? Promise.reject(new ApiError(0, 'No zone selected.'))
      : run(zoneId, signal)
}

function isStatus(error: unknown, ...statuses: number[]): boolean {
  return error instanceof ApiError && statuses.includes(error.status)
}

/* -------------------------------------------------------------------------- */
/* Identity                                                                   */
/* -------------------------------------------------------------------------- */

export function useMe(): UseQueryResult<MeResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: ({ signal }) => getMe(signal),
    retry: shouldRetry,
    staleTime: 60_000,
  })
}

export function useAccount(): UseQueryResult<AccountResponse, unknown> {
  return useQuery({
    queryKey: queryKeys.account,
    queryFn: ({ signal }) => getAccount(signal),
    retry: shouldRetry,
    staleTime: 30_000,
  })
}

export function usePlans(): UseQueryResult<Plan[], unknown> {
  return useQuery({
    queryKey: queryKeys.plans,
    queryFn: ({ signal }) => listPlans(signal),
    retry: shouldRetry,
    staleTime: 10 * 60_000,
  })
}

/**
 * `GET /tooling/info` — the protocol version and the bridge (if any) this server ships. Public and
 * per server, so it needs no zone and no auth; `getToolingInfo` sends the request unauthenticated
 * regardless of whether the caller happens to hold a token, for the same reason the route itself
 * is public (CB-WI9).
 */
export function useToolingInfo(): UseQueryResult<ToolingInfo, unknown> {
  return useQuery({
    queryKey: queryKeys.toolingInfo,
    queryFn: ({ signal }) => getToolingInfo(signal),
    retry: shouldRetry,
    staleTime: 5 * 60_000,
  })
}

export function useRotateToken(): UseMutationResult<RotateTokenResponse, unknown, void> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => rotateToken(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      void queryClient.invalidateQueries({ queryKey: queryKeys.account })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Zones                                                                      */
/* -------------------------------------------------------------------------- */

export function useZones(state: ZoneStateFilter = 'active'): UseQueryResult<ZoneSummary[], unknown> {
  return useQuery({
    queryKey: queryKeys.zones(state),
    queryFn: ({ signal }) => listZones(state, signal),
    retry: shouldRetry,
    staleTime: 30_000,
  })
}

/** Every zone mutation can change the switcher, so they all invalidate `zones`. */
function useZoneInvalidation(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.allZones })
    void queryClient.invalidateQueries({ queryKey: queryKeys.me })
  }
}

export function useCreateZone(): UseMutationResult<ZoneSummary, unknown, CreateZoneRequest> {
  const invalidate = useZoneInvalidation()
  return useMutation({ mutationFn: (body) => createZone(body), onSuccess: invalidate })
}

export function useUpdateZone(): UseMutationResult<
  ZoneSummary,
  unknown,
  { zoneId: string; body: UpdateZoneRequest }
> {
  const invalidate = useZoneInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, body }) => updateZone(zoneId, body),
    onSuccess: invalidate,
  })
}

/**
 * §19's third authority, mirrored client-side for rendering only.
 *
 * The rule is the server's (`ZoneService.isZoneAdministrator`, the single
 * chokepoint the REST and MCP layers both call): an account ADMIN passes for
 * every zone of their account; anybody else passes only for a zone whose
 * `administrator_user_id` is theirs.
 *
 * **This decides what to render, never what is allowed.** Every §19 endpoint
 * re-derives it server-side and answers 403 regardless of what the console
 * believed, so hiding a control here is a courtesy and not a control.
 */
export function isZoneAdministrator(
  me: MeResponse | undefined,
  zone: Pick<ZoneSummary, 'administrator_user_id'> | undefined,
): boolean {
  if (me === undefined || zone === undefined) return false
  if (me.role === 'ADMIN') return true
  return zone.administrator_user_id !== null && zone.administrator_user_id === me.user.id
}

/** {@link isZoneAdministrator} against the signed-in user, for a given zone. */
export function useIsZoneAdministrator(
  zone: Pick<ZoneSummary, 'administrator_user_id'> | undefined,
): boolean {
  const me = useMe()
  return isZoneAdministrator(me.data, zone)
}

export type ZoneLifecycleAction = 'deactivate' | 'activate' | 'archive'

const ZONE_LIFECYCLE: Record<ZoneLifecycleAction, (zoneId: string) => Promise<void>> = {
  deactivate: deactivateZone,
  activate: activateZone,
  archive: archiveZone,
}

export function useZoneLifecycle(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; action: ZoneLifecycleAction }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, action }) => ZONE_LIFECYCLE[action](zoneId),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.allZones })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Zone membership                                                            */
/* -------------------------------------------------------------------------- */

export function useZoneMembers(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<ZoneMemberSummary[], unknown> {
  return useQuery({
    queryKey: queryKeys.zoneMembers(zoneId),
    queryFn: zoneQuery(zoneId, listZoneMembers),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
  })
}

export function useSetZoneMember(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; userId: string; granted: boolean }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, userId, granted }) =>
      granted ? grantZoneMember(zoneId, userId) : revokeZoneMember(zoneId, userId),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.zoneMembers(zoneId) })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Account users                                                              */
/* -------------------------------------------------------------------------- */

export function useUsers(enabled = true): UseQueryResult<AccountUser[], unknown> {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: ({ signal }) => listUsers(signal),
    enabled,
    retry: shouldRetry,
    staleTime: 30_000,
  })
}

function useUsersInvalidation(): () => void {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.users })
  }
}

export function useInviteUser(): UseMutationResult<AccountUser, unknown, InviteUserRequest> {
  const invalidate = useUsersInvalidation()
  return useMutation({ mutationFn: (body) => inviteUser(body), onSuccess: invalidate })
}

export function useUpdateUserRole(): UseMutationResult<
  AccountUser,
  unknown,
  { userId: string; role: MembershipRole; billing?: boolean }
> {
  const invalidate = useUsersInvalidation()
  return useMutation({
    mutationFn: ({ userId, role, billing }) => updateUser(userId, billing === undefined ? { role } : { role, billing }),
    onSuccess: invalidate,
  })
}

export function useUserLifecycle(): UseMutationResult<
  void,
  unknown,
  { userId: string; activate: boolean }
> {
  const invalidate = useUsersInvalidation()
  return useMutation({
    mutationFn: ({ userId, activate }) =>
      activate ? activateUser(userId) : deactivateUser(userId),
    onSuccess: invalidate,
  })
}

/* -------------------------------------------------------------------------- */
/* Connections                                                                */
/* -------------------------------------------------------------------------- */

export function useConnections(
  zoneId: string | undefined,
): UseQueryResult<ConnectionSummary[], unknown> {
  return useQuery({
    queryKey: queryKeys.connections(zoneId),
    queryFn: zoneQuery(zoneId, listConnections),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    refetchInterval: CONNECTIONS_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

export function useDisconnectConnection(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; id: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, id }) => disconnectConnection(zoneId, id),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.presence(zoneId) })
    },
  })
}

/**
 * WI9. Everything a release moves: the row leaves the connection list, its channel memberships
 * go, its unread rows become DISCARDED and its history rows are renamed to `name#N` — so the
 * Messages page and the inbox are invalidated too, not just the list.
 */
export function useReleaseConnection(): UseMutationResult<
  ReleaseResponse,
  unknown,
  { zoneId: string; id: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, id }) => releaseConnection(zoneId, id),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.channels(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.presence(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(zoneId) })
    },
  })
}

/**
 * Reissues a connection id. The response carries the raw id, so it is deliberately NOT written into
 * any query cache: the caller holds it in component state for one showing and drops it. Only the
 * listing is refreshed — nothing in it changes but last_activity_at, and it is cheap.
 */
export function useReissueConnection(): UseMutationResult<
  ReissueResponse,
  unknown,
  { zoneId: string; id: string }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, id }) => reissueConnection(zoneId, id),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Pausing (§19)                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Everything a pause or unpause can change. The connection list obviously moves,
 * but so does the zone history — a `DELIVER` promotes `HELD` rows to `QUEUED`
 * and a `DISCARD` moves them to `DISCARDED`, and both are visible on the
 * Messages page — and so does the inbox, since the console's own HUMAN
 * connection can be the counterparty whose backlog was just released.
 */
function usePauseInvalidation(): (zoneId: string) => void {
  const queryClient = useQueryClient()
  return (zoneId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.messages(zoneId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(zoneId) })
  }
}

export function usePauseConnection(): UseMutationResult<
  ConnectionSummary,
  unknown,
  { zoneId: string; id: string; reason: string }
> {
  const invalidate = usePauseInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, id, reason }) => pauseConnection(zoneId, id, { reason }),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

export function useUnpauseConnection(): UseMutationResult<
  UnpauseResponse,
  unknown,
  { zoneId: string; id: string; body: UnpauseConnectionRequest }
> {
  const invalidate = usePauseInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, id, body }) => unpauseConnection(zoneId, id, body),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Police (§19)                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The zone's police seat. Enabled only for a caller the console believes to be
 * the zone administrator — everyone else would get a 403 the page has no use
 * for, and `shouldRetry` would not retry it anyway.
 */
/** The record that secrets were passed in a zone (D30). Never a value. */
export function useSecretEvents(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<SecretEvent[], unknown> {
  return useQuery({
    queryKey: [...queryKeys.police(zoneId).slice(0, 2), 'secrets'],
    queryFn: zoneQuery(zoneId, getSecretEvents),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 10_000,
  })
}

/* -------------------------------------------------------------------------- */
/* Agent file transfers (D31)                                                  */
/* -------------------------------------------------------------------------- */

/**
 * How many rows the Uploads page asks for. Exported because the page says "showing the most recent
 * 200" when it gets a full one, and two places writing 200 is how that sentence starts lying.
 */
export const UPLOADS_PAGE_SIZE = 200

/** The record of files passed in a zone, newest first. Never the bytes. */
export function useUploads(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<UploadsPage, unknown> {
  return useQuery({
    queryKey: queryKeys.uploads(zoneId),
    queryFn: ({ signal }) => listUploads(zoneId as string, { size: UPLOADS_PAGE_SIZE }, signal),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 10_000,
  })
}

/**
 * The secure files waiting for ME in this zone (SECURE_FILES decision 10).
 *
 * <p>Short `staleTime`, because the list changes under the reader for reasons they did not cause:
 * a file expires, or the sender's offer is collected on another device.
 */
export function useSecureFiles(
  zoneId: string | undefined,
): UseQueryResult<SecureFileDto[], unknown> {
  return useQuery({
    queryKey: queryKeys.secureFiles(zoneId),
    queryFn: ({ signal }) => listSecureFiles(zoneId as string, signal),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 5_000,
  })
}

/** The download log of one upload. Fetched only once its row is expanded. */
export function useUploadDownloads(
  zoneId: string | undefined,
  handle: string,
  enabled: boolean,
): UseQueryResult<DownloadEventDto[], unknown> {
  return useQuery({
    queryKey: queryKeys.uploadDownloads(zoneId, handle),
    queryFn: ({ signal }) => listUploadDownloads(zoneId as string, handle, signal),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 10_000,
  })
}

/**
 * Administrator removal of a stored file (D31 decision 9a).
 *
 * Optimistic: the row flips to DELETED at once and is put back if the server
 * refuses — the checkbox that sprang back (bug-088) is the lesson; a control
 * that half-applies is worse than a slow one. `deleted_by` is left null until
 * the refetch fills in the display name, because the console does not know it.
 */
export function useRemoveUpload(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; handle: string },
  { previous: UploadsPage | undefined }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, handle }) => removeUpload(zoneId, handle),
    onMutate: async ({ zoneId, handle }) => {
      await client.cancelQueries({ queryKey: queryKeys.uploads(zoneId) })
      const previous = client.getQueryData<UploadsPage>(queryKeys.uploads(zoneId))
      if (previous !== undefined) {
        client.setQueryData<UploadsPage>(
          queryKeys.uploads(zoneId),
          previous.map((item) =>
            item.handle === handle
              ? { ...item, status: 'DELETED', deleted_at: new Date().toISOString() }
              : item,
          ),
        )
      }
      return { previous }
    },
    onError: (_error, { zoneId }, context) => {
      if (context?.previous !== undefined) {
        client.setQueryData(queryKeys.uploads(zoneId), context.previous)
      }
    },
    onSettled: (_data, _error, { zoneId }) => {
      void client.invalidateQueries({ queryKey: queryKeys.uploads(zoneId) })
    },
  })
}

/** Sets the account's maximum upload size (null = plan maximum). ADMIN only. */
export function useSetUploadLimit(): UseMutationResult<AccountSummary, unknown, number | null> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (maxUploadBytes: number | null) => setUploadLimit(maxUploadBytes),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['account'] })
      void client.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** Replaces the account's blocked MIME patterns. ADMIN only. */
export function useSetBlockedMimeTypes(): UseMutationResult<AccountSummary, unknown, string[]> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (patterns: string[]) => setBlockedMimeTypes(patterns),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['account'] })
      void client.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/** Sets how long an unread agent secret lives. ADMIN only; the server enforces the bounds. */
export function useSetSecretLifetime(): UseMutationResult<AccountSummary, unknown, number> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (seconds: number) => setSecretLifetime(seconds),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['account'] })
      void client.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

export function useSetPresenceTimeout(): UseMutationResult<AccountSummary, unknown, number> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (seconds: number) => setPresenceTimeout(seconds),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['account'] })
      void client.invalidateQueries({ queryKey: ['me'] })
    },
  })
}

/**
 * The selectable police models, with a cost estimate per evaluation.
 *
 * Long `staleTime`: a price catalogue changes when an operator runs an UPDATE,
 * not while somebody is looking at a settings form.
 */
export function usePoliceModels(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<PoliceModelOption[], unknown> {
  return useQuery({
    queryKey: [...queryKeys.police(zoneId), 'models'],
    queryFn: zoneQuery(zoneId, getPoliceModels),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 300_000,
  })
}

export function usePolice(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<PoliceSettings, unknown> {
  return useQuery({
    queryKey: queryKeys.police(zoneId),
    queryFn: zoneQuery(zoneId, getPolice),
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

/**
 * Every police write answers with the whole updated seat, so the response is
 * written straight into the cache rather than triggering a refetch: a refetch
 * would race the write and could briefly re-render the *old* `has_api_key`.
 */
function usePoliceWrite<TVariables>(
  run: (variables: TVariables & { zoneId: string }) => Promise<PoliceSettings>,
): UseMutationResult<PoliceSettings, unknown, TVariables & { zoneId: string }> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (settings, { zoneId }) => {
      queryClient.setQueryData(queryKeys.police(zoneId), settings)
      // Designating switches policing on and changes which connection holds the
      // seat, both of which the connections table renders.
      void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
    },
  })
}

export function useUpdatePolice(): UseMutationResult<
  PoliceSettings,
  unknown,
  { zoneId: string; body: UpdatePoliceRequest }
> {
  return usePoliceWrite(({ zoneId, body }) => updatePolice(zoneId, body))
}

export function useClearPoliceApiKey(): UseMutationResult<
  PoliceSettings,
  unknown,
  { zoneId: string }
> {
  return usePoliceWrite(({ zoneId }) => clearPoliceApiKey(zoneId))
}

export function useVerifyPoliceApiKey(): UseMutationResult<
  PoliceSettings,
  unknown,
  { zoneId: string }
> {
  return usePoliceWrite(({ zoneId }) => verifyPoliceApiKey(zoneId))
}

export function useDesignatePolice(): UseMutationResult<
  PoliceSettings,
  unknown,
  { zoneId: string; connectionId: string }
> {
  return usePoliceWrite(({ zoneId, connectionId }) => designatePolice(zoneId, connectionId))
}

export function useResignPolice(): UseMutationResult<PoliceSettings, unknown, { zoneId: string }> {
  return usePoliceWrite(({ zoneId }) => resignPolice(zoneId))
}

/* -------------------------------------------------------------------------- */
/* Add-ons (§19)                                                              */
/* -------------------------------------------------------------------------- */

/** Every catalogue add-on plus this account's enrolment. Readable by any member. */
export function useAddons(): UseQueryResult<AccountAddon[], unknown> {
  return useQuery({
    queryKey: queryKeys.addons,
    queryFn: ({ signal }) => listAddons(signal),
    retry: shouldRetry,
    staleTime: 60_000,
  })
}

export function useSetAddon(): UseMutationResult<
  AccountAddon,
  unknown,
  { code: string; enabled: boolean }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ code, enabled }) => setAddon(code, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.addons })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Human presence                                                             */
/* -------------------------------------------------------------------------- */

/**
 * My HUMAN connection in this zone, or `null` when I have not joined. §18 makes
 * "not joined" a 404, which is an answer rather than a failure — it is folded
 * into the data so callers never branch on an error to render a Join button.
 */
export function usePresence(
  zoneId: string | undefined,
): UseQueryResult<ConnectionSummary | null, unknown> {
  const load = zoneQuery(zoneId, getPresence)
  return useQuery({
    queryKey: queryKeys.presence(zoneId),
    queryFn: async (context) => {
      try {
        return await load(context)
      } catch (error) {
        if (isStatus(error, 404)) return null
        throw error
      }
    },
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

/** Everything that becomes possible (or impossible) once presence changes. */
function usePresenceInvalidation(): (zoneId: string) => void {
  const queryClient = useQueryClient()
  return (zoneId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.presence(zoneId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.connections(zoneId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(zoneId) })
    void queryClient.invalidateQueries({ queryKey: queryKeys.channels(zoneId) })
  }
}

export function useJoinZone(): UseMutationResult<
  ConnectionSummary,
  unknown,
  { zoneId: string; name?: string }
> {
  const invalidate = usePresenceInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, name }) => joinZone(zoneId, name === undefined ? {} : { name }),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

export function useLeaveZone(): UseMutationResult<void, unknown, string> {
  const invalidate = usePresenceInvalidation()
  return useMutation({
    mutationFn: (zoneId) => leaveZone(zoneId),
    onSuccess: (_result, zoneId) => {
      invalidate(zoneId)
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Messaging                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The zone conversation, newest page first and older pages fetched on demand.
 *
 * The endpoint used to hand back every row a zone had ever held. That is fine at ten messages and
 * ruinous at a hundred thousand — and because a channel broadcast writes one row per member, the
 * row count grows faster than the conversation does.
 *
 * `pages` arrive newest-page-first, so they are reversed and flattened to give one oldest-first
 * list. Collapsing fan-outs happens over that whole list rather than per page: a broadcast split
 * across a page boundary must still render as one message.
 *
 * `channel`: server-side filter, omit for everything, `'none'` for direct messages only, or a
 * channel UUID. Included in the query key so switching channels refetches instead of showing the
 * previous channel's cached page.
 */
export function useMessages(
  zoneId: string | undefined,
  anchor?: number,
  channel?: string,
  window: TimeBounds = {},
): UseInfiniteQueryResult<InfiniteData<ZoneHistoryPage>, unknown> {
  return useInfiniteQuery({
    queryKey: queryKeys.messages(zoneId, anchor, channel, window),
    queryFn: ({ pageParam, signal }) =>
      listMessages(
        zoneId as string,
        pageParam === undefined
          ? { after: anchor, channel, ...window }
          : { before: pageParam, channel, ...window },
        signal,
      ),
    initialPageParam: undefined as number | undefined,
    // Only backwards: the newest page is the entry point and older pages are pulled in as the user
    // scrolls up. Null means the beginning of the conversation has been reached.
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_before ?? undefined) : undefined,
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    // Polling only ever refreshes the FIRST page, which is where new messages land.
    refetchInterval: MESSAGES_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

/**
 * The jump rail: one entry per message, no bodies, capped server-side.
 *
 * `channel`: omit for everything, `'none'` for direct messages only, or a channel UUID.
 */
export function useMessageIndex(
  zoneId: string | undefined,
  channel?: string,
  window: TimeBounds = {},
): UseQueryResult<ZoneMessageIndex, unknown> {
  return useQuery({
    queryKey: queryKeys.messageIndex(zoneId, channel, window),
    queryFn: ({ signal }) =>
      zoneId === undefined
        ? Promise.reject(new ApiError(0, 'No zone selected.'))
        : listMessageIndex(zoneId, channel, window, signal),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    refetchInterval: MESSAGES_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

/** The managed-police balance (D27). Account-wide, so not zone-scoped. */
export function useBalance(refetchMs: number | false = false): UseQueryResult<AccountBalance, unknown> {
  return useQuery({
    queryKey: queryKeys.balance(),
    queryFn: ({ signal }) => getBalance(signal),
    refetchInterval: refetchMs,
    retry: shouldRetry,
  })
}

/** The ledger. Newest first, and every charge carries its full basis. */
export function useBalanceTransactions(
  refetchMs: number | false = false,
): UseQueryResult<BalanceTransactionRow[], unknown> {
  return useQuery({
    queryKey: queryKeys.balanceTransactions(),
    queryFn: ({ signal }) => listBalanceTransactions(signal),
    refetchInterval: refetchMs,
    retry: shouldRetry,
  })
}

/**
 * The PLAN subscription (`GET /api/billing`, ADMIN only). `refetchMs` polls while the console waits
 * for a webhook to land (checkout return); the page never grants anything itself.
 */
export function useBilling(options: { enabled?: boolean; refetchMs?: number | false } = {}): UseQueryResult<
  BillingSummary,
  unknown
> {
  return useQuery({
    queryKey: ['billing'],
    queryFn: ({ signal }) => getBilling(signal),
    enabled: options.enabled ?? true,
    refetchInterval: options.refetchMs ?? false,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

/** Starts Embedded Checkout for a plan; the caller mounts the returned client secret. */
export function useCheckout(): UseMutationResult<
  SubscribeResult,
  unknown,
  { planCode: string; interval: 'month' | 'year'; useCheckout?: boolean }
> {
  return useMutation({
    mutationFn: ({ planCode, interval, useCheckout }) => startCheckout(planCode, interval, useCheckout === true),
  })
}

/** Chooses the zones to keep through an over-limit downgrade. */
export function useKeepZones(): UseMutationResult<OverLimit, unknown, string[]> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (zoneIds) => keepZones(zoneIds),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['billing'] })
    },
  })
}

/** Starts a police top-up Embedded Checkout; the credit arrives by webhook, so nothing is invalidated here. */
export function useTopUpCheckout(): UseMutationResult<
  CheckoutSession,
  unknown,
  { amountUsd: number; saveCard: boolean }
> {
  return useMutation({ mutationFn: ({ amountUsd, saveCard }) => startTopUp(amountUsd, saveCard) })
}

/** What can be refunded now, plus this account's refund requests. ADMIN only. */
export function useRefundable(enabled = true): UseQueryResult<Refundable, unknown> {
  return useQuery({
    queryKey: ['billing', 'refundable'],
    queryFn: ({ signal }) => getRefundable(signal),
    enabled,
    retry: shouldRetry,
  })
}

/** Asks for a refund. The amount is reserved server-side, so the figures are stale afterwards. */
export function useRequestRefund(): UseMutationResult<
  RefundRequest,
  unknown,
  { amountUsd: number; reason: string }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ amountUsd, reason }) => createRefundRequest(amountUsd, reason),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['billing', 'refundable'] })
    },
  })
}

export function usePoliceRecharge(): UseQueryResult<PoliceRecharge, unknown> {
  return useQuery({
    queryKey: ['billing', 'police-recharge'],
    queryFn: ({ signal }) => getPoliceRecharge(signal),
    retry: shouldRetry,
  })
}

export function useSavePoliceRecharge(): UseMutationResult<
  PoliceRecharge,
  unknown,
  { enabled: boolean; threshold_usd?: number; amount_usd?: number }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (settings) => setPoliceRecharge(settings),
    onSuccess: (saved) => {
      client.setQueryData(['billing', 'police-recharge'], saved)
    },
  })
}

/**
 * The conversation rail (D26).
 *
 * Polls on the same cadence as the messages themselves, because a new conversation appearing is
 * exactly as interesting as a new message and the payload carries no bodies.
 *
 * `channel`: omit for everything, `'none'` for direct conversations only, or a channel UUID.
 * Included in the query key so switching channels refetches instead of showing the previous
 * channel's cached page.
 */
export function useConversations(
  zoneId: string | undefined,
  archived: boolean,
  channel?: string,
  window: TimeBounds = {},
): UseQueryResult<ConversationIndex, unknown> {
  return useQuery({
    queryKey: queryKeys.conversations(zoneId, archived, channel, window),
    queryFn: ({ signal }) =>
      listConversations(zoneId as string, { archived, size: 200, channel, ...window }, signal),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    refetchInterval: MESSAGES_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

/**
 * Text search, grouped by conversation.
 *
 * Deliberately NOT polled. A search is a question asked once; re-running it underneath the reader
 * every few seconds would move results while they were being read.
 */
export function useSearch(
  zoneId: string | undefined,
  query: string,
  archived: boolean,
): UseQueryResult<SearchResults, unknown> {
  const trimmed = query.trim()
  return useQuery({
    queryKey: queryKeys.search(zoneId, trimmed, archived),
    queryFn: ({ signal }) => searchMessages(zoneId as string, trimmed, archived, signal),
    enabled: zoneId !== undefined && trimmed.length > 0,
    retry: shouldRetry,
  })
}

/** Archive or unarchive. Invalidates both rails, since the row moves between them. */
/**
 * Closes the account, for good.
 *
 * Invalidates everything rather than patching the cache: after closing, every
 * zone is unreachable and every connection is gone, so a cache selectively
 * updated would keep rendering a working console for an account that no longer
 * works. A full refetch shows the truth.
 */
export function useCloseAccount(): UseMutationResult<AccountSummary, unknown, void> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => closeAccount(),
    onSuccess: () => {
      void client.invalidateQueries()
    },
  })
}

export function useSetConversationArchived(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; conversationId: string; archived: boolean }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, conversationId, archived }) =>
      archived
        ? archiveConversation(zoneId, conversationId)
        : unarchiveConversation(zoneId, conversationId),
    onSuccess: (_result, { zoneId }) => {
      void client.invalidateQueries({ queryKey: ['zones', zoneId, 'conversations'] })
    },
  })
}

/** My queue. Empty rather than an error while I have no presence in the zone. */
export function useInbox(
  zoneId: string | undefined,
  enabled = true,
): UseQueryResult<QueuedMessage[], unknown> {
  const load = zoneQuery(zoneId, getInbox)
  return useQuery({
    queryKey: queryKeys.inbox(zoneId),
    queryFn: async (context) => {
      try {
        return await load(context)
      } catch (error) {
        if (isStatus(error, 404, 409)) return []
        throw error
      }
    },
    enabled: enabled && zoneId !== undefined,
    retry: shouldRetry,
    refetchInterval: MESSAGES_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

export function useSendMessage(): UseMutationResult<
  SendMessageResponse,
  unknown,
  { zoneId: string; body: SendMessageRequest }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ zoneId, body }) => sendMessage(zoneId, body),
    onSuccess: (_result, { zoneId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages(zoneId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.inbox(zoneId) })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Channels                                                                   */
/* -------------------------------------------------------------------------- */

export function useChannels(
  zoneId: string | undefined,
): UseQueryResult<ChannelSummary[], unknown> {
  return useQuery({
    queryKey: queryKeys.channels(zoneId),
    queryFn: zoneQuery(zoneId, listChannels),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    refetchInterval: CHANNELS_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

function useChannelsInvalidation(): (zoneId: string) => void {
  const queryClient = useQueryClient()
  return (zoneId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.channels(zoneId) })
  }
}

export function useCreateChannel(): UseMutationResult<
  ChannelSummary,
  unknown,
  { zoneId: string; name: string }
> {
  const invalidate = useChannelsInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, name }) => createChannel(zoneId, { name }),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

export function useJoinChannel(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; id: string }
> {
  const invalidate = useChannelsInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, id }) => joinChannel(zoneId, id),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

export function useDeleteChannel(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; id: string }
> {
  const invalidate = useChannelsInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, id }) => deleteChannel(zoneId, id),
    onSuccess: (_result, { zoneId }) => {
      invalidate(zoneId)
    },
  })
}

/** `GET /api/zones/{zoneId}/channels/{channelId}/members` — who is joined right now. */
export function useChannelMembers(
  zoneId: string | undefined,
  channelId: string | undefined,
): UseQueryResult<ChannelMember[], unknown> {
  return useQuery({
    queryKey: queryKeys.channelMembers(zoneId, channelId),
    queryFn: ({ signal }) =>
      zoneId === undefined || channelId === undefined
        ? Promise.reject(new ApiError(0, 'No channel selected.'))
        : listChannelMembers(zoneId, channelId, signal),
    enabled: zoneId !== undefined && channelId !== undefined,
    retry: shouldRetry,
  })
}

/**
 * `POST /api/zones/{zoneId}/channels/{channelId}/leave` — my HUMAN connection leaves.
 *
 * Idempotent, so nothing is rolled back on failure. Invalidates the channel list — `member_count`
 * moves — and this channel's own member list, which is otherwise the only place the removal shows.
 */
export function useLeaveChannel(): UseMutationResult<
  void,
  unknown,
  { zoneId: string; channelId: string }
> {
  const queryClient = useQueryClient()
  const invalidate = useChannelsInvalidation()
  return useMutation({
    mutationFn: ({ zoneId, channelId }) => leaveChannel(zoneId, channelId),
    onSuccess: (_result, { zoneId, channelId }) => {
      invalidate(zoneId)
      void queryClient.invalidateQueries({ queryKey: queryKeys.channelMembers(zoneId, channelId) })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Tooling                                                                    */
/* -------------------------------------------------------------------------- */

export function useZoneMcpJson(zoneId: string | undefined): UseQueryResult<string, unknown> {
  return useQuery({
    queryKey: queryKeys.mcpJson(zoneId),
    queryFn: zoneQuery(zoneId, getZoneMcpJson),
    enabled: zoneId !== undefined,
    retry: shouldRetry,
    staleTime: 5 * 60_000,
  })
}

/* --- feedback ------------------------------------------------------------- */

export function useSubmitFeedback(): UseMutationResult<
  SubmitFeedbackResponse,
  unknown,
  SubmitFeedbackRequest
> {
  return useMutation({ mutationFn: (body) => submitFeedback(body) })
}

/** The staff inbox, keyset-paged. A 403 (not staff) is not retried. */
export function useStaffFeedback(
  filters: StaffFeedbackFilters,
  enabled: boolean,
): UseInfiniteQueryResult<InfiniteData<StaffFeedbackPage>, unknown> {
  return useInfiniteQuery({
    queryKey: queryKeys.staffFeedback(filters),
    queryFn: ({ pageParam, signal }) => listStaffFeedback(filters, pageParam, 50, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    enabled,
    retry: shouldRetry,
  })
}

/** One report in full (the list carries no bodies). */
export function useStaffFeedbackItem(
  id: string | undefined,
): UseQueryResult<StaffFeedbackItem, unknown> {
  return useQuery({
    queryKey: queryKeys.staffFeedbackItem(id),
    queryFn: ({ signal }) => getStaffFeedback(id as string, signal),
    enabled: id !== undefined,
    retry: shouldRetry,
  })
}

export function useTriageFeedback(): UseMutationResult<
  StaffFeedbackItem,
  unknown,
  { id: string; body: TriageFeedbackRequest }
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }) => triageFeedback(id, body),
    onSuccess: (item) => {
      client.setQueryData(queryKeys.staffFeedbackItem(item.feedback_id), item)
      void client.invalidateQueries({ queryKey: ['staff', 'feedback'] })
    },
  })
}

/* -------------------------------------------------------------------------- */
/* Billing in the app (2026-10-BILLING-IN-APP)                                 */
/* -------------------------------------------------------------------------- */

function useBillingInvalidation(): () => void {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: ['billing'] })
  }
}

/** The publishable key; fetched once per session. */
export function useBillingConfig(enabled = true): UseQueryResult<BillingConfig, unknown> {
  return useQuery({
    queryKey: ['billing', 'config'],
    queryFn: ({ signal }) => getBillingConfig(signal),
    enabled,
    retry: shouldRetry,
    staleTime: Infinity,
  })
}

/** Invoices, newest first, `Load more` appends Stripe's next page. */
export function useInvoices(
  kind: InvoiceKind | null,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<InvoicePage>, unknown> {
  return useInfiniteQuery({
    queryKey: ['billing', 'invoices', kind],
    queryFn: ({ pageParam, signal }) => listInvoices({ cursor: pageParam, limit: 20, kind }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.has_more ? last.next_cursor : undefined),
    enabled,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

/** The newest invoices, for the app-wide past-due banner. */
export function useOpenInvoices(enabled: boolean): UseQueryResult<Invoice[], unknown> {
  return useQuery({
    queryKey: ['billing', 'open-invoices'],
    queryFn: async ({ signal }) => (await listInvoices({ limit: 20 }, signal)).invoices.filter((i) => i.status === 'open'),
    enabled,
    retry: shouldRetry,
    staleTime: 60_000,
  })
}

export function usePayInvoice(): UseMutationResult<Invoice, unknown, { invoiceId: string; paymentMethod?: string }> {
  const invalidate = useBillingInvalidation()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, paymentMethod }) => payInvoice(invoiceId, paymentMethod),
    onSettled: () => {
      invalidate()
      void client.invalidateQueries({ queryKey: queryKeys.me })
    },
  })
}

export function useUpcoming(enabled = true): UseQueryResult<UpcomingInvoice, unknown> {
  return useQuery({
    queryKey: ['billing', 'upcoming'],
    queryFn: ({ signal }) => getUpcoming(signal),
    enabled,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

export function usePaymentMethods(enabled = true): UseQueryResult<PaymentMethodCard[], unknown> {
  return useQuery({
    queryKey: ['billing', 'payment-methods'],
    queryFn: ({ signal }) => listPaymentMethods(signal),
    enabled,
    retry: shouldRetry,
    staleTime: 15_000,
  })
}

export function useCreateSetupIntent(): UseMutationResult<{ client_secret: string }, unknown, void> {
  return useMutation({ mutationFn: () => createSetupIntent() })
}

/** Refreshes the card list after the Payment Element saved a card. */
export function useCardsChanged(): () => void {
  return useBillingInvalidation()
}

export function useSetDefaultCard(): UseMutationResult<PaymentMethodCard[], unknown, string> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: (id) => setDefaultPaymentMethod(id), onSettled: invalidate })
}

export function useRemoveCard(): UseMutationResult<PaymentMethodCard[], unknown, string> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: (id) => removePaymentMethod(id), onSettled: invalidate })
}

export function useBillingDetails(enabled = true): UseQueryResult<BillingDetails, unknown> {
  return useQuery({
    queryKey: ['billing', 'details'],
    queryFn: ({ signal }) => getBillingDetails(signal),
    enabled,
    retry: shouldRetry,
  })
}

export function useUpdateBillingDetails(): UseMutationResult<
  BillingDetails,
  unknown,
  { name: string; address: BillingAddress }
> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: (body) => updateBillingDetails(body), onSuccess: invalidate })
}

export function useTaxIds(enabled = true): UseQueryResult<TaxId[], unknown> {
  return useQuery({
    queryKey: ['billing', 'tax-ids'],
    queryFn: ({ signal }) => listTaxIds(signal),
    enabled,
    retry: shouldRetry,
  })
}

export function useAddTaxId(): UseMutationResult<TaxId, unknown, { type: string; value: string }> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: (body) => addTaxId(body), onSuccess: invalidate })
}

export function useDeleteTaxId(): UseMutationResult<void, unknown, string> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: (id) => deleteTaxId(id), onSuccess: invalidate })
}

export function usePlanChangePreview(): UseMutationResult<
  PlanChangePreview,
  unknown,
  { plan: string; interval: 'month' | 'year' }
> {
  return useMutation({ mutationFn: ({ plan, interval }) => previewPlanChange(plan, interval) })
}

export function useChangePlan(): UseMutationResult<
  PlanChangeResult,
  unknown,
  { plan: string; interval: 'month' | 'year' }
> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: ({ plan, interval }) => changePlanNow(plan, interval), onSettled: invalidate })
}

/**
 * Cancel and resume answer with Stripe's own state, but our copy of it only changes when the webhook
 * lands. A refetch straight after the POST therefore returns the OLD state and the page would not change
 * until a reload. So: show the answer now, and refetch a little later, once the webhook has had time.
 */
function useSubscriptionStateSettle(): (state: SubscriptionState) => void {
  const client = useQueryClient()
  return (state) => {
    client.setQueryData<BillingSummary>(['billing'], (old) =>
      old === undefined
        ? old
        : { ...old, cancel_at_period_end: state.cancel_at_period_end, current_period_end: state.current_period_end ?? old.current_period_end })
    for (const delay of [2_000, 6_000]) {
      window.setTimeout(() => { void client.invalidateQueries({ queryKey: ['billing'] }) }, delay)
    }
  }
}

export function useCancelSubscription(): UseMutationResult<SubscriptionState, unknown, void> {
  const settle = useSubscriptionStateSettle()
  return useMutation({ mutationFn: () => cancelSubscription(), onSuccess: settle })
}

export function useResumeSubscription(): UseMutationResult<SubscriptionState, unknown, void> {
  const settle = useSubscriptionStateSettle()
  return useMutation({ mutationFn: () => resumeSubscription(), onSuccess: settle })
}

export function useUndoScheduledChange(): UseMutationResult<void, unknown, void> {
  const invalidate = useBillingInvalidation()
  return useMutation({ mutationFn: () => undoScheduledChange(), onSuccess: invalidate })
}
